// ═══════════════════════════════════════════════════════════════
//  AdminLogin.jsx  — MakeBeeTrip · Premium Redesign
//  Light theme, inline CSS only, no backend changes.
// ═══════════════════════════════════════════════════════════════

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminAuth } from "./AdminAuthContext";
import { Eye, EyeOff } from "lucide-react";

// ── Design Tokens ─────────────────────────────────────────────
const T = {
  bg:         "#F7F9FC",
  surface:    "#FFFFFF",
  ink:        "#0D1B2A",
  ink2:       "#1E3A4C",
  muted:      "#5C7080",
  faint:      "#9BB0BE",
  line:       "#E4ECF2",
  emerald:    "#1DB680",
  emeraldDk:  "#159663",
  emeraldLt:  "#E6F7F1",
  amber:      "#D97706",
  amberLt:    "#FFFBEB",
  rose:       "#E04343",
  roseLt:     "#FFF1F1",
  blue:       "#2563EB",
  blueLt:     "#EFF6FF",
  shadowSm:   "0 1px 4px rgba(13,27,42,0.07)",
  shadowMd:   "0 4px 20px rgba(13,27,42,0.10)",
  shadowLg:   "0 16px 48px rgba(13,27,42,0.14)",
};

// ── Spinner ───────────────────────────────────────────────────
function Spinner() {
  return (
    <>
      <style>{`@keyframes _mbt_spin { to { transform: rotate(360deg); } }`}</style>
      <span style={{
        display: "inline-block", width: 16, height: 16, flexShrink: 0,
        border: `2.5px solid ${T.emeraldLt}`,
        borderTopColor: T.emerald,
        borderRadius: "50%",
        animation: "_mbt_spin 0.75s linear infinite",
      }} />
    </>
  );
}

// // ── Decorative Bee SVG ────────────────────────────────────────
// function BeeLogo({ size = 44 }) {
//   return (
//     <div style={{
//       width: size, height: size, borderRadius: 14,
//       background: `linear-gradient(135deg, ${T.emerald}, ${T.emeraldDk})`,
//       display: "flex", alignItems: "center", justifyContent: "center",
//       boxShadow: `0 6px 20px ${T.emerald}55`,
//       fontSize: size * 0.52, lineHeight: 1,
//     }}></div>
//   );
// }

// ── Input Field ───────────────────────────────────────────────
function Field({ label, type, value, onChange, onKeyDown, placeholder, autoFocus, autoComplete, rightEl }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ marginBottom: 18 }}>
      <label style={{
        display: "block", fontSize: 11, fontWeight: 700,
        color: T.faint, textTransform: "uppercase", letterSpacing: "0.7px", marginBottom: 7,
      }}>{label}</label>
      <div style={{ position: "relative" }}>
        <input
          type={type}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          autoFocus={autoFocus}
          autoComplete={autoComplete}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            width: "100%", boxSizing: "border-box",
            padding: rightEl ? "12px 44px 12px 16px" : "12px 16px",
            border: `1.5px solid ${focused ? T.emerald : T.line}`,
            borderRadius: 12, fontSize: 14, outline: "none",
            color: T.ink, background: focused ? T.surface : T.bg,
            transition: "border-color 0.2s, background 0.2s",
            boxShadow: focused ? `0 0 0 3px ${T.emerald}18` : "none",
          }}
        />
        {rightEl && (
          <div style={{
            position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)",
          }}>{rightEl}</div>
        )}
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────
export default function AdminLogin() {
  const { adminLogin } = useAdminAuth();
  const navigate = useNavigate();

  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [error,    setError]    = useState("");
  const [loading,  setLoading]  = useState(false);
  const [showPass, setShowPass] = useState(false);

  const handleSubmit = () => {
    setError("");
    if (!email.trim())    return setError("Email is required");
    if (!password.trim()) return setError("Password is required");
    setLoading(true);
setTimeout(async () => {
  const result = await adminLogin(email.trim(), password);
      if (result.ok) {
        navigate("/admin", { replace: true });
      } else {
        setError(result.error || "Invalid credentials");
        setLoading(false);
      }
    }, 400);
  };

  const onKeyDown = (e) => { if (e.key === "Enter") handleSubmit(); };

  return (
    <div style={{
      minHeight: "100vh",
      background: T.bg,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 24,
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', sans-serif",
      position: "relative",
      overflow: "hidden",
    }}>
      {/* Subtle background orbs */}
      <div style={{
        position: "fixed", top: -120, right: -120,
        width: 480, height: 480, borderRadius: "50%",
        background: `radial-gradient(circle, ${T.emerald}12 0%, transparent 70%)`,
        pointerEvents: "none",
      }} />
      <div style={{
        position: "fixed", bottom: -80, left: -80,
        width: 360, height: 360, borderRadius: "50%",
        background: `radial-gradient(circle, ${T.blue}08 0%, transparent 70%)`,
        pointerEvents: "none",
      }} />

      {/* Card */}
      <div style={{
        width: "100%", maxWidth: 440,
        background: T.surface,
        borderRadius: 24,
        border: `1px solid ${T.line}`,
        boxShadow: T.shadowLg,
        overflow: "hidden",
        position: "relative", zIndex: 1,
      }}>

        {/* Header Strip */}
        <div style={{
          padding: "32px 36px 28px",
          borderBottom: `1px solid ${T.line}`,
          display: "flex", flexDirection: "column", alignItems: "center",
          gap: 14, textAlign: "center",
          background: `linear-gradient(160deg, ${T.surface} 0%, ${T.bg} 100%)`,
        }}>
          <img alt="MakeBeeTrip Logo" src="/static/media/logo.b333e6319c98b5cdf1fe.png"></img>
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            
              <span style={{
                fontSize: 9, fontWeight: 500, textTransform: "uppercase", letterSpacing: "1.2px",
                background: T.emerald, color: "#fff",
                padding: "3px 9px", borderRadius: 20, marginTop: -65, marginLeft: 290,
              }}>Admin</span>
            </div>
            <p style={{ fontSize: 13, color: T.faint, margin: "6px 0 0", fontWeight: 400 }}>
              Sign in to access the control panel
            </p>
          </div>
        </div>

        {/* Form Body */}
        <div style={{ padding: "28px 36px 36px" }}>

          {/* Error */}
          {error && (
            <div style={{
              background: T.roseLt, border: `1px solid ${T.rose}44`,
              color: T.rose, padding: "11px 16px", borderRadius: 12,
              fontSize: 13, fontWeight: 500, marginBottom: 20,
              display: "flex", alignItems: "center", gap: 8,
            }}>
              <span style={{ fontSize: 16 }}>⚠</span> {error}
            </div>
          )}

          <Field
            label="Admin Email"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="admin@makebeentrip.com"
            autoFocus
            autoComplete="username"
          />

          <Field
            label="Password"
            type={showPass ? "text" : "password"}
            value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="••••••••"
            autoComplete="current-password"
            rightEl={
              <button
                type="button"
                onClick={() => setShowPass(v => !v)}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  color: T.faint, fontSize: 15, padding: 0, lineHeight: 1,
                  display: "flex", alignItems: "center",
                }}
                aria-label={showPass ? "Hide password" : "Show password"}
              >{showPass ? (
        <EyeOff size={18} />
      ) : (
        <Eye size={18} />
      )}</button>
            }
          />

          {/* CTA */}
          <button
            onClick={handleSubmit}
            disabled={loading}
            style={{
              width: "100%", marginTop: 8,
              padding: "13px",
              background: loading
                ? `linear-gradient(135deg, ${T.emeraldDk}, ${T.emerald})`
                : `linear-gradient(135deg, ${T.emerald}, ${T.emeraldDk})`,
              color: "#fff",
              border: "none", borderRadius: 12,
              fontSize: 14, fontWeight: 700,
              cursor: loading ? "not-allowed" : "pointer",
              letterSpacing: "0.1px",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              boxShadow: loading ? "none" : `0 4px 16px ${T.emerald}44`,
              transition: "all 0.2s",
              opacity: loading ? 0.85 : 1,
            }}
            onMouseEnter={e => { if (!loading) { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = `0 8px 24px ${T.emerald}55`; } }}
            onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = `0 4px 16px ${T.emerald}44`; }}
          >
            {loading ? <><Spinner /> Signing in…</> : "Sign in →"}
          </button>

          <p style={{
            textAlign: "center", fontSize: 12,
            color: T.faint, margin: "20px 0 0",
          }}>
            Authorised personnel only
          </p>
        </div>
      </div>
    </div>
  );
}